
// src/hooks/useLabOngoingTests.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  LAB_STORAGE_KEYS,
  loadLabStorage,
  removeLabStorage,
  saveLabStorage,
} from "../utils/labStorage";

import {
  LAB_ONGOING_TESTS,
} from "../data/labMockData";

import type {
  CompleteTestPayload,
  CompletedTest,
  OngoingTest,
  TestStatus,
} from "../types/lab-context.types";

interface UseLabOngoingTestsOptions {
  /**
   * Called when an ongoing test is completed and should
   * be added to the completed-tests collection.
   */
  onTestCompleted?: (
    test: CompletedTest,
  ) => void;


   // Temporary reviewer information.
   // Replace these values with authenticated laboratory
   // or medical-review user information later.

  currentReviewerName?: string;
  currentReviewerLicense?: string;
}

interface UseLabOngoingTestsReturn {
  ongoingTests: OngoingTest[];

  addOngoingTest: (
    test: OngoingTest,
  ) => void;

  updateTestStatus: (
    testId: string,
    newStatus: TestStatus,
  ) => void;

  completeTest: (
    test: OngoingTest,
    payload: CompleteTestPayload,
  ) => void;

  clearOngoingTests: () => void;
  resetOngoingTests: () => void;
}

const TEST_STATUS_PROGRESS: Record<
  TestStatus,
  number
> = {
  sample_collection: 10,
  processing: 45,
  analyzing: 75,
  ready: 100,
  on_hold: 45,
};

/**
 * Creates a safe PDF filename from the test name and patient ID.
 */
const createResultFilename = (
  testType: string,
  patientId: string,
): string => {
  const normalizedTestType = testType
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `${normalizedTestType}_${patientId}.pdf`;
};

/**
 * Calculates elapsed processing time between the start and
 * completion timestamps.
 */
const calculateTurnaroundTime = (
  startTime: string,
  completedAt: Date,
): string => {
  const startedAt = new Date(startTime);

  if (
    Number.isNaN(
      startedAt.getTime(),
    )
  ) {
    return "0h 0m";
  }

  const elapsedMilliseconds =
    Math.max(
      0,
      completedAt.getTime() -
        startedAt.getTime(),
    );

  const elapsedHours =
    Math.floor(
      elapsedMilliseconds /
        (1000 * 60 * 60),
    );

  const elapsedMinutes =
    Math.floor(
      (elapsedMilliseconds %
        (1000 * 60 * 60)) /
        (1000 * 60),
    );

  return `${elapsedHours}h ${elapsedMinutes}m`;
};

/**
 * Combines findings and optional laboratory notes into a
 * single result summary.
 */
const createFindingsSummary = (
  payload: CompleteTestPayload,
): string => {
  const sections = [
    payload.findings.trim(),

    payload.recommendations?.trim()
      ? `Recommendations: ${payload.recommendations.trim()}`
      : null,

    payload.criticalValues?.trim()
      ? `Critical values: ${payload.criticalValues.trim()}`
      : null,

    payload.notes?.trim()
      ? `Notes: ${payload.notes.trim()}`
      : null,
  ].filter(
    (
      section,
    ): section is string =>
      Boolean(section),
  );

  return sections.join("\n\n");
};

/**
 * Manages laboratory tests currently moving through the
 * processing workflow.
 */
export const useLabOngoingTests = ({
  onTestCompleted,
  currentReviewerName =
    "Pending medical review",
  currentReviewerLicense = "Pending",
}: UseLabOngoingTestsOptions = {}): UseLabOngoingTestsReturn => {
  const [ongoingTests, setOngoingTests] =
    useState<OngoingTest[]>(() =>
      loadLabStorage(
        LAB_STORAGE_KEYS.ONGOING_TESTS,
        LAB_ONGOING_TESTS,
      ),
    );

  useEffect(() => {
    saveLabStorage(
      LAB_STORAGE_KEYS.ONGOING_TESTS,
      ongoingTests,
    );
  }, [ongoingTests]);

  /**
   * Adds a newly accepted request to the ongoing-test queue.
   *
   * Existing IDs are ignored to prevent duplicate tests.
   */
  const addOngoingTest = useCallback(
    (
      test: OngoingTest,
    ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [],
  );

  /**
   * Updates both the workflow status and its matching progress.
   */
  const updateTestStatus = useCallback(
    (
      testId: string,
      newStatus: TestStatus,
    ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [],
  );

  /**
   * Removes a test from the ongoing queue and converts it
   * into a completed result.
   */
  const completeTest = useCallback(
    (
      test: OngoingTest,
      payload: CompleteTestPayload,
    ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
    [
      currentReviewerLicense,
      currentReviewerName,
      onTestCompleted,
    ],
  );

  const clearOngoingTests =
    useCallback((): void => {
      removeLabStorage(
        LAB_STORAGE_KEYS.ONGOING_TESTS,
      );

      setOngoingTests([]);
    }, []);

  const resetOngoingTests =
    useCallback((): void => {
      setOngoingTests(
        LAB_ONGOING_TESTS,
      );
    }, []);

  return {
    ongoingTests,

    addOngoingTest,
    updateTestStatus,
    completeTest,

    clearOngoingTests,
    resetOngoingTests,
  };
};

